import { HlsVideoPlayer } from '../workout/hls-video-player'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { hoverPreviewCoordinator } from 'src/service/hover-preview-coordinator'
import { FeaturedSurface, MixpanelEvent, mixpanelService } from 'src/service/mixpanel-service'

/** Long enough that a pointer crossing the shelf starts nothing. */
const INTENT_DELAY_MS = 400

/**
 * Pin the preview to the lowest rendition. This is a public marketing page and the preview is
 * decoration — it must not compete for bandwidth with the class someone is actually watching.
 * `HlsVideoPlayer` otherwise defaults to `startLevel: 2`.
 */
const PREVIEW_HLS_CONFIG = { startLevel: 0, capLevelToPlayerSize: true }

/** Pointer devices only — a touch screen has no hover, and tap-to-preview is out of scope. */
const supportsHover = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches

interface FeaturedWorkoutHoverPreviewProps {
  videoUrl?: string
  /** Whether the pointer is currently over the card. */
  active: boolean
  workoutId: string
  surface: FeaturedSurface
}

/**
 * A muted, in-place preview of the card's own class.
 *
 * There is no preview asset: `previewVideoUrl` exists in `dao/core-dao.ts` and in old V1 Bruno
 * fixtures but was never added to the backend entity, model or DTO, so it is always null. This
 * deliberately reuses the full `videoUrl`, muted and pinned to the lowest rendition.
 *
 * Nothing is rendered — and so no stream is ever fetched — on a device without hover, or
 * before the intent delay elapses.
 */
export const FeaturedWorkoutHoverPreview = ({
  videoUrl,
  active,
  workoutId,
  surface,
}: FeaturedWorkoutHoverPreviewProps): React.ReactElement => {
  const [isPlaying, setIsPlaying] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  // Stable identity across renders, so the coordinator can tell this preview from its siblings.
  const stopRef = useRef<() => void>(() => setIsPlaying(false))

  const stop = useCallback((): void => {
    clearTimeout(timerRef.current)
    setIsPlaying(false)
    hoverPreviewCoordinator.release(stopRef.current)
  }, [])

  useEffect(() => {
    if (!active || !videoUrl || !supportsHover()) {
      stop()
      return
    }

    // Hover is the ONLY way a preview starts. An autoplaying lead card was built and rejected
    // on review — it read as noise on a sign-in page, and it put a stream on every page view
    // of the app's highest-traffic public surface. Do not reintroduce it.
    timerRef.current = setTimeout(() => {
      hoverPreviewCoordinator.claim(stopRef.current)
      setIsPlaying(true)
      // Fired when the preview actually begins, not on mouseenter — a pointer crossing the
      // shelf must not register as intent.
      mixpanelService.trackFeatured(MixpanelEvent.FEATURED_WORKOUT_PREVIEW_STARTED, { workoutId, surface })
    }, INTENT_DELAY_MS)

    return () => clearTimeout(timerRef.current)
  }, [active, videoUrl, stop, workoutId, surface])

  // Also tear down on unmount: navigating away mid-hover must not leave a stream running or
  // the coordinator holding a callback into an unmounted component.
  useEffect(() => stop, [stop])

  if (!isPlaying || !videoUrl) return <></>

  return (
    <div className="absolute inset-0 z-0 overflow-hidden rounded-md" data-testid="featured-workout-preview">
      <HlsVideoPlayer
        src={videoUrl}
        autoPlay
        muted
        controls={false}
        hlsConfig={PREVIEW_HLS_CONFIG}
        height="100%"
        className="w-full h-full object-cover"
      />
    </div>
  )
}
